import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { FileText, Link as LinkIcon, Code } from 'lucide-react';
import { GlobVector } from '@glob/glob';
import { Button } from '@glob/ui';
import { useItems } from '../../features/items/useItems';
import type { CachedItem } from '../../lib/cache/idb';
import type { ItemType } from '../../features/items/data/items';
import { useT } from '../../i18n';
import type { MessageKey } from '../../i18n';

const TYPE_ICON = { memo: FileText, link: LinkIcon, snippet: Code } as const;

const NEW_ACTIONS: { type: ItemType; icon: typeof FileText; labelKey: MessageKey }[] = [
  { type: 'memo', icon: FileText, labelKey: 'items.new-memo' },
  { type: 'link', icon: LinkIcon, labelKey: 'items.new-link' },
  { type: 'snippet', icon: Code, labelKey: 'items.new-snippet' },
];

function NewButtons({
  onNew,
  size = 'md',
}: {
  onNew: (type: ItemType) => void;
  size?: 'sm' | 'md';
}) {
  const t = useT();
  return (
    <div className="flex items-center gap-2">
      {NEW_ACTIONS.map(({ type, icon: Icon, labelKey }, i) => (
        <Button
          key={type}
          size={size}
          variant={i === 0 ? 'primary' : 'ghost'}
          onClick={() => onNew(type)}
        >
          <Icon size={size === 'sm' ? 14 : 16} aria-hidden />
          {t(labelKey)}
        </Button>
      ))}
    </div>
  );
}

function EmptyState({ onNew }: { onNew: (type: ItemType) => void }) {
  const t = useT();
  return (
    <section className="h-full flex flex-col items-center justify-center text-center gap-4 p-8">
      <GlobVector mood="shy" size={120} />
      <h1 className="text-2xl font-display tracking-tight">{t('empty.title')}</h1>
      <p className="max-w-sm text-sm text-text-3 leading-relaxed">{t('empty.body')}</p>
      <NewButtons onNew={onNew} />
    </section>
  );
}

function ItemRow({ item }: { item: CachedItem }) {
  const t = useT();
  const Icon = TYPE_ICON[item.item_type];
  const title = (item.decrypted_meta['title'] as string) || t('items.untitled');
  return (
    <Link
      to="/app/item/$itemId"
      params={{ itemId: item.uuid }}
      className="flex items-center gap-3 px-3 py-2.5 rounded-md border border-transparent hover:border-border hover:bg-bg-2 transition-colors"
    >
      <span className="w-0.5 self-stretch rounded-full bg-highlight/70" aria-hidden />
      <Icon size={16} className="text-text-3 shrink-0" aria-hidden />
      <span className="flex-1 truncate text-sm text-text-1">{title}</span>
    </Link>
  );
}

function SpaceIndex() {
  const t = useT();
  const navigate = useNavigate();
  const { data: items, isLoading } = useItems();
  const onNew = (type: ItemType) => void navigate({ to: '/app/item/new/$type', params: { type } });

  if (!isLoading && (!items || items.length === 0)) {
    return <EmptyState onNew={onNew} />;
  }

  return (
    <section className="max-w-3xl mx-auto p-6 space-y-4">
      <header className="flex items-center justify-between gap-3">
        <span className="text-sm text-text-3">
          {items
            ? items.length === 1
              ? t('items.count-one')
              : t('items.count', { count: items.length })
            : ''}
        </span>
        <NewButtons onNew={onNew} size="sm" />
      </header>
      <div className="flex flex-col gap-0.5">
        {items?.map((item) => (
          <ItemRow key={item.uuid} item={item} />
        ))}
      </div>
    </section>
  );
}

export const Route = createFileRoute('/_authed/app/')({
  component: SpaceIndex,
});
